/**
 * KnowledgeGraphVisualization.jsx
 * ================================
 * Interactive D3.js knowledge graph visualization.
 */
import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Slider } from '../ui/slider';
import { 
  Network, ZoomIn, ZoomOut, Maximize2, RefreshCw,
  Filter, Download, Play, Pause
} from 'lucide-react';

const ENTITY_COLORS = {
  person: '#3b82f6',      // blue
  organization: '#8b5cf6', // purple
  document: '#22c55e',    // green
  amount: '#eab308',      // yellow
  date: '#f97316',        // orange
  location: '#ec4899',    // pink
  clause: '#06b6d4',      // cyan
  vendor: '#14b8a6',      // teal
  default: '#6b7280'      // gray
};

const KnowledgeGraphVisualization = ({ graphData, onNodeClick, onRefresh }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
  const [simulation, setSimulation] = useState(null);
  const [isRunning, setIsRunning] = useState(true);
  const [linkStrength, setLinkStrength] = useState([50]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [filter, setFilter] = useState(null);

  // Process graph data
  const processedData = React.useMemo(() => {
    if (!graphData || !graphData.nodes) {
      // Generate sample data if none provided
      return {
        nodes: [],
        links: []
      };
    }

    const nodes = graphData.nodes.map((node, i) => ({
      id: node.id || `node-${i}`,
      label: node.label || node.value || `Node ${i}`,
      type: node.type || 'default',
      count: node.count || 1,
      ...node
    }));

    const links = (graphData.links || graphData.edges || []).map((link, i) => ({
      source: link.source,
      target: link.target,
      weight: link.weight || link.count || 1,
      type: link.type || 'related'
    }));

    return { nodes, links };
  }, [graphData]);

  // Filter nodes by type
  const filteredData = React.useMemo(() => {
    if (!filter) return processedData;

    const filteredNodes = processedData.nodes.filter(n => n.type === filter);
    const nodeIds = new Set(filteredNodes.map(n => n.id));
    const filteredLinks = processedData.links.filter(
      l => nodeIds.has(l.source?.id || l.source) && nodeIds.has(l.target?.id || l.target)
    );

    return { nodes: filteredNodes, links: filteredLinks };
  }, [processedData, filter]);

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width: width || 800, height: height || 500 });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // D3 Force Simulation
  useEffect(() => {
    if (!svgRef.current || filteredData.nodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { width, height } = dimensions;

    // Create zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Main group for zoom/pan
    const g = svg.append('g');

    // Create arrow marker for directed links
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 20)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .append('path')
      .attr('d', 'M 0,-5 L 10,0 L 0,5')
      .attr('fill', '#999');

    // Create simulation
    const sim = d3.forceSimulation(filteredData.nodes)
      .force('link', d3.forceLink(filteredData.links)
        .id(d => d.id)
        .distance(100)
        .strength(linkStrength[0] / 100)
      )
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(30));

    setSimulation(sim);

    // Draw links
    const link = g.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(filteredData.links)
      .enter()
      .append('line')
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', d => Math.sqrt(d.weight))
      .attr('marker-end', 'url(#arrowhead)');

    // Draw nodes
    const node = g.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(filteredData.nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .call(d3.drag()
        .on('start', (event, d) => {
          if (!event.active) sim.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) sim.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        })
      )
      .on('click', (event, d) => {
        setSelectedNode(d);
        onNodeClick?.(d);
      });

    // Node circles
    node.append('circle')
      .attr('r', d => Math.max(10, Math.min(25, 8 + d.count)))
      .attr('fill', d => ENTITY_COLORS[d.type] || ENTITY_COLORS.default)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer');

    // Node labels
    node.append('text')
      .text(d => d.label.length > 15 ? d.label.substring(0, 15) + '...' : d.label)
      .attr('x', 0)
      .attr('y', d => Math.max(10, Math.min(25, 8 + d.count)) + 15)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#374151');

    // Tooltip on hover
    node.append('title')
      .text(d => `${d.label}\nType: ${d.type}\nOccurrences: ${d.count}`);

    // Update positions on tick
    sim.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    return () => sim.stop();
  }, [filteredData, dimensions, linkStrength, onNodeClick]);

  // Control simulation
  const toggleSimulation = () => {
    if (simulation) {
      if (isRunning) {
        simulation.stop();
      } else {
        simulation.alpha(0.3).restart();
      }
      setIsRunning(!isRunning);
    }
  };

  const resetZoom = () => {
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition().duration(750).call(
        d3.zoom().transform,
        d3.zoomIdentity
      );
    }
  };

  // Get unique entity types for filter
  const entityTypes = [...new Set(processedData.nodes.map(n => n.type))];

  return (
    <Card className="h-full" data-testid="knowledge-graph-visualization">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Network className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Interactive Knowledge Graph</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={toggleSimulation}>
              {isRunning ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <Button variant="outline" size="sm" onClick={resetZoom}>
              <Maximize2 className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Controls */}
        <div className="flex items-center gap-4 flex-wrap">
          {/* Entity Type Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <div className="flex gap-1 flex-wrap">
              <Button
                size="sm"
                variant={filter === null ? "default" : "outline"}
                onClick={() => setFilter(null)}
                className="h-7 text-xs"
              >
                All
              </Button>
              {entityTypes.map(type => (
                <Button
                  key={type}
                  size="sm"
                  variant={filter === type ? "default" : "outline"}
                  onClick={() => setFilter(type)}
                  className="h-7 text-xs"
                  style={{
                    backgroundColor: filter === type ? ENTITY_COLORS[type] : undefined,
                    borderColor: ENTITY_COLORS[type]
                  }}
                >
                  {type}
                </Button>
              ))}
            </div>
          </div>

          {/* Link Strength Slider */}
          <div className="flex items-center gap-2 min-w-[150px]">
            <span className="text-xs text-muted-foreground">Link Strength:</span>
            <Slider
              value={linkStrength}
              onValueChange={setLinkStrength}
              max={100}
              step={10}
              className="w-24"
            />
          </div>
        </div>

        {/* Legend */}
        <div className="flex gap-3 flex-wrap">
          {Object.entries(ENTITY_COLORS).filter(([k]) => k !== 'default').map(([type, color]) => (
            <div key={type} className="flex items-center gap-1 text-xs">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="capitalize">{type}</span>
            </div>
          ))}
        </div>

        {/* Graph Container */}
        <div 
          ref={containerRef} 
          className="border rounded-lg bg-slate-50 relative"
          style={{ minHeight: '400px' }}
        >
          {filteredData.nodes.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <Network className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No data to visualize</p>
                <p className="text-sm">Process documents to build the knowledge graph</p>
              </div>
            </div>
          ) : (
            <svg
              ref={svgRef}
              width="100%"
              height="400"
              style={{ display: 'block' }}
            />
          )}
        </div>

        {/* Stats */}
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">Nodes:</span>
            <Badge variant="outline">{filteredData.nodes.length}</Badge>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">Links:</span>
            <Badge variant="outline">{filteredData.links.length}</Badge>
          </div>
          {selectedNode && (
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">Selected:</span>
              <Badge
                style={{ backgroundColor: ENTITY_COLORS[selectedNode.type] }}
                className="text-white"
              >
                {selectedNode.label}
              </Badge>
            </div>
          )}
        </div>

        {/* Selected Node Details */}
        {selectedNode && (
          <div className="p-3 bg-muted/30 rounded-lg">
            <h4 className="font-medium mb-2">Selected: {selectedNode.label}</h4>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Type:</span>
                <span className="ml-2 capitalize">{selectedNode.type}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Occurrences:</span>
                <span className="ml-2">{selectedNode.count}</span>
              </div>
              {selectedNode.documents && (
                <div>
                  <span className="text-muted-foreground">In Documents:</span>
                  <span className="ml-2">{selectedNode.documents.length}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default KnowledgeGraphVisualization;
